import { CustomKeyAttributes } from '@loyalty/shared-common/profile-api-client';
import { Fields } from '@react-awesome-query-builder/core';
import { ParentCategoryName } from '@loyalty/shared-common';
import { BaseConfigSettings } from '../fields';
import { excludedOperators } from '../settings';
import { profileFields } from './profile-fields';
import { shouldBeApositiveNumber } from './should-be-apositive-number';
import { translateParentCategory } from '../../../../rule-table-constants';

export function qualifioEngageCampaign(
  settings: BaseConfigSettings,
  customKeys?: CustomKeyAttributes[],
): Fields {
  const { t } = settings;
  return {
    event: {
      label: t('rule.criteria.event'),
      type: '!group',
      subfields: {
        campaignId: {
          label: t('rule.criteria.engage.campaign'),
          type: 'campaign',
          excludeOperators: excludedOperators,
          valueSources: ['value'],
        },
        campaignType: {
          label: t('rule.criteria.engage.campaign.type'),
          type: 'text',
          excludeOperators: excludedOperators,
          valueSources: ['value'],
        },
        campaignThemes: {
          label: t('rule.criteria.engage.thematics'),
          type: '!group',
          subfields: {
            code: {
              type: 'text',
              excludeOperators: excludedOperators,
              label: t('rule.criteria.code'),
              valueSources: ['value'],
              allowCustomValues: true,
            },
            description: {
              type: 'text',
              excludeOperators: excludedOperators,
              label: t('rule.criteria.description'),
              valueSources: ['value'],
              allowCustomValues: true,
            },
          },
        },
        /*
            This field is still in the payload, but make it invisble to user to select
            channelUuid: {
              label: t('rule.criteria.engage.channel.id'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },*/
        globalVariables: {
          label: t('rule.criteria.engage.global.variables'),
          type: '!group',
          subfields: {
            key: {
              label: t('rule.criteria.key'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
            value: {
              label: t('rule.criteria.value'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
          },
        },
        score: {
          label: t('rule.criteria.engage.score'),
          type: '!group',
          subfields: {
            kind: {
              label: t('rule.criteria.kind'),
              type: 'select',
              valueSources: ['value'],
              fieldSettings: {
                listValues: [
                  { value: 'POINT', title: 'Point' },
                  { value: 'TIME', title: 'Time' },
                  { value: 'DISTANCE', title: 'Distance' },
                ],
              },
            },
            value: {
              label: t('rule.criteria.value'),
              type: 'number',
              fieldSettings: {
                validateValue: shouldBeApositiveNumber,
              },
              valueSources: ['value'],
              preferWidgets: ['number'],
            },
          },
        },
        optins: {
          label: t('rule.criteria.engage.optins'),
          type: '!group',
          subfields: {
            id: {
              label: t('rule.criteria.id'),
              type: 'number',
              valueSources: ['value'],
            },
            variable: {
              label: t('rule.criteria.engage.variable'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
            label: {
              label: t('rule.criteria.engage.label'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
            value: {
              label: t('rule.criteria.value'),
              type: 'select',
              valueSources: ['value'],
              fieldSettings: {
                listValues: [
                  { value: '1', title: 'Yes' },
                  { value: '0', title: 'No' },
                  { value: '', title: 'Unknown' },
                ],
              },
            },
            categories: {
              label: t('rule.criteria.categories'),
              type: '!group',
              subfields: {
                name: {
                  label: t('rule.criteria.optin.categories.name'),
                  type: 'text',
                  excludeOperators: excludedOperators,
                  valueSources: ['value'],
                },
                technicalName: {
                  label: t('rule.criteria.optin.categories.technicalName'),
                  type: 'text',
                  excludeOperators: excludedOperators,
                  valueSources: ['value'],
                },
                parentName: {
                  label: t('rule.criteria.optin.categories.parentName'),
                  type: 'select',
                  valueSources: ['value'],
                  fieldSettings: {
                    listValues: Object.values(ParentCategoryName).map(type => ({
                      value: type,
                      title: translateParentCategory(t, type),
                    })),
                  },
                },
              },
            },
          },
        },
        personalityTest: {
          label: t('rule.criteria.personalityTest'),
          type: '!group',
          subfields: {
            group: {
              label: t('rule.criteria.personalityTest.group'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
            groupName: {
              label: t('rule.criteria.personalityTest.groupName'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
          },
        },
        websiteName: {
          label: t('rule.criteria.website'),
          type: 'text',
          excludeOperators: excludedOperators,
          valueSources: ['value'],
        },
        variables: {
          label: t('rule.criteria.engage.variables'),
          type: '!group',
          subfields: {
            key: {
              label: t('rule.criteria.key'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
            value: {
              label: t('rule.criteria.value'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
          },
        },
        uniqueCode: {
          label: t('rule.criteria.engage.uniqueCode'),
          type: 'text',
          excludeOperators: ['proximity'],
          valueSources: ['value'],
        },
        burnedCodes: {
          label: t('rule.criteria.engage.burnedCodes'),
          type: '!group',
          subfields: {
            variable: {
              label: t('rule.criteria.engage.variable'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
            code: {
              label: t('rule.criteria.code'),
              type: 'text',
              excludeOperators: ['proximity'],
              valueSources: ['value'],
            },
          },
        },
        timestamp: {
          label: settings.t('rule.criteria.date'),
          type: 'datetime',
          preferWidgets: ['date'],
        },
      },
    },
    profile: {
      label: t('rule.criteria.profile'),
      type: '!group',
      subfields: profileFields(settings, customKeys),
    },
  };
}
